import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/App";
import { Brain, ChevronRight, Sparkles } from "lucide-react";

/**
 * DailyReviewCard - surfaces the SM-2 review queue on the dashboard.
 * Shows nothing if the user has never started a mastery loop.
 */
const DailyReviewCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/study/mastery")
      .then((res) => { if (alive) setData(res); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading || !data) return null;
  const due = data.review_due_count || 0;
  const completedTopics = (data.by_subject || []).reduce((s, x) => s + x.topics_completed, 0);

  // Don't show if user has never used the mastery loop AND has no due reviews
  if (due === 0 && completedTopics === 0) return null;

  if (due === 0) {
    return (
      <div
        data-testid="daily-review-card"
        className="rounded-2xl p-5 border border-border bg-card flex items-center gap-4"
      >
        <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">All caught up</div>
          <div className="text-xs text-muted-foreground">No reviews due today. Tackle a new topic instead.</div>
        </div>
        <Link
          to="/learn"
          data-testid="daily-review-new-topic"
          className="text-primary text-sm font-medium hover:underline flex-shrink-0"
        >
          Learn →
        </Link>
      </div>
    );
  }

  return (
    <Link
      to="/review"
      data-testid="daily-review-card"
      className="block rounded-2xl p-5 border border-primary/30 bg-gradient-to-r from-primary/10 to-primary/5 hover:from-primary/15 transition-all"
    >
      <div className="flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
          <Brain className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">{due} review card{due === 1 ? "" : "s"} due today</div>
          <div className="text-xs text-muted-foreground">
            5-min review of questions you previously missed. Spaced repetition = better retention.
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-primary flex-shrink-0" />
      </div>
    </Link>
  );
};

export default DailyReviewCard;
